import {
  ATOMIC_TOOTH_SURFACES,
  getAtomicSurfacesForTooth,
  inferToothSurfaceArchetypeFromProcedureText,
  sortSurfacesByClinicalOrder,
  ToothSurfaceArchetype,
} from '@components/layout/Modals/Appointment/Appointment.constant'
import { IToothDetails } from '@dataTypes/appointment.type'
import { IProcedureDentalCondition } from '@dataTypes/treatmentPlan.types'

const ATOMIC_SURFACE_SET = new Set<string>(ATOMIC_TOOTH_SURFACES)

export type ToothSurfaceDisplayEntry = {
  area_type: number | null
  area?: string | null
  tooth?: string | null
  surf?: string | null
}

export const MAX_SELECTABLE_SURFACES_PER_TOOTH = 6

export type ProcedureSurfaceSelectionRules = {
  requiresSurfaceSelection: boolean
  surfaceCount: number | null
  surfaceCountIsMin: boolean
  archetype: ToothSurfaceArchetype
}

const SURFACE_COUNT_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
}

const isTruthyDbFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === '1' || value === 'true'

const parseSurfaceCount = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** Fallback when procedure_dental_conditions is missing or not yet backfilled. */
export const inferSurfaceSelectionFromProcedureText = (
  text: string | null | undefined,
): Pick<
  ProcedureSurfaceSelectionRules,
  'requiresSurfaceSelection' | 'surfaceCount' | 'surfaceCountIsMin'
> | null => {
  if (text === null || text === undefined || text.trim() === '') {
    return null
  }
  const lower = text.toLowerCase()
  if (/\bfour or more surfaces?\b/.test(lower)) {
    return {
      requiresSurfaceSelection: true,
      surfaceCount: 4,
      surfaceCountIsMin: true,
    }
  }
  const match = lower.match(/\b(one|two|three|four)\s+surfaces?\b/)
  if (!match) {
    return null
  }
  const surfaceCount = SURFACE_COUNT_WORDS[match[1]]
  if (!surfaceCount) {
    return null
  }
  return {
    requiresSurfaceSelection: true,
    surfaceCount,
    surfaceCountIsMin: false,
  }
}

export const isValidSurfaceCountForProcedure = (
  selectedCount: number,
  surfaceCount: number | null,
  surfaceCountIsMin: boolean,
): boolean => {
  if (selectedCount <= 0) {
    return false
  }
  if (selectedCount > MAX_SELECTABLE_SURFACES_PER_TOOTH) {
    return false
  }
  if (surfaceCount === null || surfaceCount <= 0) {
    return true
  }
  if (surfaceCountIsMin) {
    return selectedCount >= surfaceCount
  }
  return selectedCount === surfaceCount
}

export const canAddAnotherSurface = (
  currentCount: number,
  surfaceCount: number | null,
  surfaceCountIsMin: boolean,
): boolean => {
  if (currentCount >= MAX_SELECTABLE_SURFACES_PER_TOOTH) {
    return false
  }
  if (
    !surfaceCountIsMin &&
    surfaceCount !== null &&
    currentCount >= surfaceCount
  ) {
    return false
  }
  return true
}

export const getSurfaceSelectionHintText = (
  rules: ProcedureSurfaceSelectionRules,
): string | null => {
  if (!rules.requiresSurfaceSelection) {
    return null
  }
  const archHint =
    rules.archetype === 'anterior'
      ? 'Anterior surfaces only. '
      : rules.archetype === 'posterior'
        ? 'Posterior surfaces only. '
        : ''
  if (rules.surfaceCount === null || rules.surfaceCount <= 0) {
    return `${archHint}Select at least one surface per tooth.`
  }
  if (rules.surfaceCountIsMin) {
    return `${archHint}Select ${rules.surfaceCount} or more surfaces per tooth.`
  }
  const plural = rules.surfaceCount === 1 ? '' : 's'
  return `${archHint}Select exactly ${rules.surfaceCount} surface${plural} per tooth.`
}

export const resolveProcedureSurfaceArchetype = (
  procedureDentalConditions: IProcedureDentalCondition[] | undefined,
  descriptionFallback: string | null | undefined,
): ToothSurfaceArchetype => {
  const mappedArchetype = procedureDentalConditions?.find(
    (condition) =>
      condition.surface_archetype === 'anterior' ||
      condition.surface_archetype === 'posterior',
  )?.surface_archetype
  if (mappedArchetype === 'anterior' || mappedArchetype === 'posterior') {
    return mappedArchetype
  }
  return inferToothSurfaceArchetypeFromProcedureText(descriptionFallback)
}

export const resolveProcedureSurfaceSelectionRules = (
  procedureDentalConditions: IProcedureDentalCondition[] | undefined,
  descriptionFallback: string | null | undefined,
): ProcedureSurfaceSelectionRules => {
  const archetype = resolveProcedureSurfaceArchetype(
    procedureDentalConditions,
    descriptionFallback,
  )
  const mapping =
    procedureDentalConditions?.find((condition) =>
      isTruthyDbFlag(condition.requires_surface_selection),
    ) ?? procedureDentalConditions?.[0]
  if (isTruthyDbFlag(mapping?.requires_surface_selection)) {
    return {
      requiresSurfaceSelection: true,
      surfaceCount: parseSurfaceCount(mapping?.surface_count),
      surfaceCountIsMin: isTruthyDbFlag(mapping?.surface_count_is_min),
      archetype,
    }
  }
  const inferredFromDescription =
    inferSurfaceSelectionFromProcedureText(descriptionFallback)
  if (inferredFromDescription) {
    return {
      ...inferredFromDescription,
      archetype,
    }
  }
  return {
    requiresSurfaceSelection: false,
    surfaceCount: null,
    surfaceCountIsMin: false,
    archetype,
  }
}

export const isToothRowSurfaceCountValid = (
  surf: string[] | undefined,
  rules: ProcedureSurfaceSelectionRules,
): boolean => {
  if (!rules.requiresSurfaceSelection) {
    return true
  }
  const count = surf?.length ?? 0
  return isValidSurfaceCountForProcedure(
    count,
    rules.surfaceCount,
    rules.surfaceCountIsMin,
  )
}

export const sortAtomicSurfaces = (surfaces: string[]): string[] =>
  sortSurfacesByClinicalOrder(surfaces)

/** Split legacy combined codes (MOD) into atomic letters; dedupe in canonical order. */
export const normalizeSurfToAtomic = (surf: string[] | undefined): string[] => {
  if (!surf || surf.length === 0) {
    return []
  }
  const seen = new Set<string>()
  const result: string[] = []
  for (const entry of surf) {
    const trimmed = entry.trim().toUpperCase()
    if (!trimmed) {
      continue
    }
    if (trimmed.length === 1 && ATOMIC_SURFACE_SET.has(trimmed)) {
      if (!seen.has(trimmed)) {
        seen.add(trimmed)
        result.push(trimmed)
      }
      continue
    }
    for (const char of trimmed) {
      const upper = char.toUpperCase()
      if (ATOMIC_SURFACE_SET.has(upper) && !seen.has(upper)) {
        seen.add(upper)
        result.push(upper)
      }
    }
  }
  return sortAtomicSurfaces(result)
}

/** Keep only surfaces valid for this tooth's anatomy and procedure archetype. */
export const pruneSurfForTooth = (
  toothNumber: number,
  surf: string[] | undefined,
  archetype: ToothSurfaceArchetype,
): string[] => {
  const allowed = new Set(getAtomicSurfacesForTooth(toothNumber, archetype))
  return normalizeSurfToAtomic(surf).filter((code) => allowed.has(code))
}

export const formatAtomicSurfacesCompact = (surfaces: string[]): string => {
  return sortAtomicSurfaces(normalizeSurfToAtomic(surfaces)).join('')
}

export const formatToothWithSurfaces = (
  tooth: string | number,
  surfaces: string[],
): string => {
  const compact = formatAtomicSurfacesCompact(surfaces)
  if (!compact) {
    return String(tooth)
  }
  return `${tooth}/${compact}`
}

export const formatToothDetailsToothLabel = (detail: IToothDetails): string => {
  if (detail.tooth_num === undefined || detail.tooth_num === null) {
    return detail.area ?? ''
  }
  const atomic = normalizeSurfToAtomic(detail.surf)
  return formatToothWithSurfaces(detail.tooth_num, atomic)
}

export const formatToothSurfaceEntriesForDisplay = (
  entries: ToothSurfaceDisplayEntry[],
): string => {
  if (entries.length === 0) {
    return ''
  }
  const nonToothLabels: string[] = []
  const surfacesByTooth = new Map<number, string[]>()
  for (const entry of entries) {
    if (entry.area_type !== 1 || !entry.tooth) {
      if (entry.area) {
        nonToothLabels.push(entry.area)
      }
      continue
    }
    const toothNumber = parseInt(entry.tooth.trim(), 10)
    if (Number.isNaN(toothNumber)) {
      continue
    }
    const existing = surfacesByTooth.get(toothNumber) ?? []
    if (entry.surf) {
      existing.push(...normalizeSurfToAtomic([entry.surf]))
    }
    surfacesByTooth.set(toothNumber, existing)
  }
  const toothLabels = Array.from(surfacesByTooth.entries())
    .sort(([a], [b]) => a - b)
    .map(([toothNum, surfaces]) => formatToothWithSurfaces(toothNum, surfaces))
  return [...nonToothLabels, ...toothLabels].filter(Boolean).join(', ')
}
